import React from 'react';
import { Loader2, AlertCircle, Image } from 'lucide-react';
import FeaturedAlbumsSection from './SelectionCriteria/FeaturedAlbumsSection';
import AlbumBrowserSection from './SelectionCriteria/AlbumBrowserSection';
import TagSearchSection from './SelectionCriteria/TagSearchSection';
import DateRangeSection from './SelectionCriteria/DateRangeSection';

const SelectionCriteria = ({
  photos,
  loading,
  criteriaCollapsed,
  setCriteriaCollapsed,

  // Albums data and handlers
  albums,
  fetchingAlbums,
  selectedAlbum,
  setSelectedAlbum,
  getFeaturedAlbums,
  handleAlbumSelect,

  // Quick actions
  handleQuickAction,

  // Tags data and handlers
  tags,
  setTags,
  handleTagSearch,

  // Date handling
  uploadStart,
  uploadEnd,
  takenStart,
  takenEnd,
  handleDateChange,

  // Other
  fetchPhotos
}) => {
  const featuredAlbums = getFeaturedAlbums();

  return (
    <div className="bg-white p-6 rounded-lg shadow mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Selection Criteria</h2>
        {photos.length > 0 && (
          <button
            onClick={() => setCriteriaCollapsed(!criteriaCollapsed)}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
          >
            <span>{criteriaCollapsed ? 'Expand' : 'Collapse'}</span>
            <svg
              className={`w-4 h-4 transition-transform ${criteriaCollapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>

      {!criteriaCollapsed && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Choose an Album</label>
            {fetchingAlbums ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Loading albums...</span>
              </div>
            ) : albums.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <AlertCircle className="h-6 w-6 mr-2" />
                <span>No albums found</span>
              </div>
            ) : (
              <>
                <FeaturedAlbumsSection
                  featuredAlbums={featuredAlbums}
                  selectedAlbumId={selectedAlbum}
                  onSelect={handleAlbumSelect}
                />

                <AlbumBrowserSection
                  albums={albums}
                  selectedAlbumId={selectedAlbum}
                  onSelect={handleAlbumSelect}
                />
              </>
            )}

            {selectedAlbum && (
              <div className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-200">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Selected album:</span>
                  <span className="ml-1">
                    {albums.find(album => album.id === selectedAlbum)?.title || 'Unknown'}
                  </span>
                  <button
                    onClick={() => setSelectedAlbum('')}
                    className="ml-2 text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear
                  </button>
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Quick Photo Selections</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
              <button
                onClick={() => handleQuickAction('uploaded-today')}
                disabled={loading}
                className="p-4 bg-gradient-to-br from-orange-50 to-yellow-50 border border-orange-200 rounded-lg hover:from-orange-100 hover:to-yellow-100 hover:border-orange-300 transition-all duration-200 text-left disabled:opacity-50"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-orange-500 rounded-lg text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Today</h3>
                    <p className="text-xs text-gray-600">Uploaded today</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleQuickAction('this-week')}
                disabled={loading}
                className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300 transition-all duration-200 text-left disabled:opacity-50"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-500 rounded-lg text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">This Week</h3>
                    <p className="text-xs text-gray-600">Recent uploads</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleQuickAction('recent-uploads')}
                disabled={loading}
                className="p-4 bg-gradient-to-br from-green-50 to-teal-50 border border-green-200 rounded-lg hover:from-green-100 hover:to-teal-100 hover:border-green-300 transition-all duration-200 text-left disabled:opacity-50"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-500 rounded-lg text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Last 30 Days</h3>
                    <p className="text-xs text-gray-600">Recent uploads</p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <TagSearchSection
            tags={tags}
            setTags={setTags}
            onSearch={handleTagSearch}
            loading={loading}
          />

          <DateRangeSection
            uploadStart={uploadStart}
            uploadEnd={uploadEnd}
            takenStart={takenStart}
            takenEnd={takenEnd}
            onDateChange={handleDateChange}
          />

          {!selectedAlbum && !tags.trim() && !uploadStart && !uploadEnd && !takenStart && !takenEnd && photos.length === 0 && (
            <div className="text-center pt-6 border-t border-gray-200">
              <button
                onClick={fetchPhotos}
                disabled={loading}
                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-bold text-lg rounded-xl hover:bg-blue-700 hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                    Loading Photos...
                  </>
                ) : (
                  <>
                    <Image className="mr-3 h-5 w-5" />
                    Select Photos to Get Started
                  </>
                )}
              </button>
              <p className="text-sm text-gray-500 mt-2">Choose an album, tags, or quick action above to begin</p>
            </div>
          )}
        </div>
      )}

      {criteriaCollapsed && photos.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {selectedAlbum && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Album: {albums.find(album => album.id === selectedAlbum)?.title || 'Unknown'}
            </span>
          )}
          {tags.trim() && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Tags: {tags}
            </span>
          )}
          {(uploadStart || uploadEnd) && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Upload: {uploadStart || '...'} to {uploadEnd || '...'}
            </span>
          )}
          {(takenStart || takenEnd) && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
              Taken: {takenStart || '...'} to {takenEnd || '...'}
            </span>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Loading photos...</span>
        </div>
      )}

      {!loading && photos.length === 0 && (selectedAlbum || tags.trim() || uploadStart || uploadEnd || takenStart || takenEnd) && (
        <div className="flex items-center justify-center text-amber-600 bg-amber-50 px-4 py-3 rounded-md border border-amber-200 mt-6">
          <AlertCircle className="mr-2 h-5 w-5 flex-shrink-0" />
          <div className="text-sm">
            <span className="font-medium">No photos found.</span>
            <span className="ml-1">Try different selection criteria: adjust date ranges, remove specific albums, or clear tag filters.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectionCriteria;
